import {Component, ErrorInfo, ReactNode} from 'react';
import {Card} from 'primereact/card';
import {Button} from 'primereact/button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  retryCount: number;
}

class ErrorBoundary extends Component<Props, State> {
  private maxRetries = 3;

  public state: State = {
    hasError: false,
    retryCount: 0,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    // Filter out object-to-primitive errors
    if (error.message && error.message.includes('Cannot convert object to primitive value')) {
      console.warn('Suppressing object-to-primitive error in ErrorBoundary');
      return {hasError: false}; // Don't treat this as an error
    }

    return {hasError: true, error};
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Filter out object-to-primitive errors
    if (error.message && error.message.includes('Cannot convert object to primitive value')) {
      console.warn('ErrorBoundary suppressing object-to-primitive error:', error);
      return;
    }

    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleReset = () => {
    if (this.state.retryCount < this.maxRetries) {
      this.setState({
        hasError: false,
        error: undefined,
        errorInfo: undefined,
        retryCount: this.state.retryCount + 1,
      });
    } else {
      this.handleReload();
    }
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const canRetry = this.state.retryCount < this.maxRetries;

      return (
        <div className="error-boundary">
          <Card
            title="Oops! Something went wrong"
            className="error-boundary__card"
            style={{margin: '2rem', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto'}}
          >
            <div className="error-boundary__content">
              <p>
                We're sorry, but something unexpected happened. {canRetry ? 'You can try again or' : 'Please'} refresh
                the page.
              </p>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="error-boundary__details" style={{marginTop: '1rem'}}>
                  <summary>Error Details (Development Only)</summary>
                  <pre
                    style={{
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.8rem',
                      background: '#f5f5f5',
                      padding: '1rem',
                      borderRadius: '4px',
                      marginTop: '1rem',
                      overflow: 'auto',
                      maxHeight: '200px',
                    }}
                  >
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </details>
              )}

              <div
                className="error-boundary__actions"
                style={{
                  marginTop: '1.5rem',
                  display: 'flex',
                  gap: '1rem',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}
              >
                <Button label="Refresh Page" icon="pi pi-refresh" onClick={this.handleReload} severity="help" />
                {canRetry && (
                  <Button
                    label={`Try Again (${this.maxRetries - this.state.retryCount} left)`}
                    icon="pi pi-replay"
                    onClick={this.handleReset}
                    outlined
                  />
                )}
              </div>
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
